import { FetchWithAuth } from "digimaker-ui/util";
import moment, { unix } from "moment";
import * as React from "react";
import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import Select from "react-select";
import { EthContext } from "eth-common/EthContext";
import ReactTooltip from "react-tooltip";

type IndicatorDataType = {
  id: string;
  indicator: string;
  years: Array<{ year: number; value: number | null }>;
  comment: string;
  target_value: number | null;
};

export const FieldIndicator4A3 = (props: {
  data: any;
  definition: any;
  mode: string;
}) => {
  const { data, definition, mode } = props;

  const initData = data
    ? typeof data === "string"
      ? JSON.parse(data)
      : data
    : [];

  const [allIndicators, setAllIndicators] = useState<any[]>([]);

  const [indicatorData, setIndicatorData] = useState<IndicatorDataType[]>(
    initData || []
  );

  const context = useContext<any>(EthContext);

  const reportYear = context && context.currentYear ? context.currentYear : 0;

  const [addYearTo, setAddYearTo] = useState("");

  const yearAddedRef = useRef<HTMLSelectElement>(null);

  const loadIndicators = async () => {
    const categories = await FetchWithAuth("eth/indicator3a/category/list", {
      method: "GET",
    });
    const indicators = await FetchWithAuth("eth/indicator3a/list", {
      method: "GET",
    });

    const list = indicators.data.map((indicator) => {
      const category = categories.data.find(
        (category) => category.id === indicator.category
      );

      return {
        ...indicator,
        category_name: category.name,
      };
    });
    setAllIndicators(list);
  };

  useEffect(() => {
    loadIndicators();
  }, []);

  if (mode === "edit" && !reportYear) {
    return <></>;
  }

  const addIndicator = (indicator: any) => {
    setIndicatorData([
      ...indicatorData,
      {
        id: moment().unix() + "",
        indicator: indicator.id,
        years: [
          {
            year: reportYear,
            value: null,
          },
          {
            year: reportYear - 1,
            value: null,
          },
        ],
        comment: "",
        target_value: null,
      },
    ]);
  };

  const getRemainYears = (id: string) => {
    const record = indicatorData.find((record) => record.id === id);
    const remainYears: number[] = [];
    if (record) {
      for (let i = 0; i < 5; i++) {
        const year = reportYear - i;
        if (!record.years.find((item) => item.year === year)) {
          remainYears.push(year);
        }
      }
    }
    return remainYears;
  };

  const deleteIndicator = (id: string) => {
    setIndicatorData(indicatorData.filter((indicator) => indicator.id !== id));
  };

  const getIndicatorDef = (id: number) => {
    const indicatorData = allIndicators.find((item) => item.id === id);
    return indicatorData || {};
  };

  const addYear = (id: string) => {
    setAddYearTo(id);
  };

  const changeValue = (id: string, key: string, value: any) => {
    setIndicatorData(
      indicatorData.map((record) => {
        if (record.id === id) {
          return { ...record, [key]: value };
        }
        return record;
      })
    );
  };

  const changeYearValue = (
    id: string,
    year: number,
    key: string,
    value: any
  ) => {
    setIndicatorData(
      indicatorData.map((record) => {
        if (record.id === id) {
          return {
            ...record,
            years: record.years.map((yearRecord) =>
              yearRecord.year === year
                ? { ...yearRecord, [key]: value }
                : yearRecord
            ),
          };
        }
        return record;
      })
    );
  };

  const addYearConfirm = () => {
    if (yearAddedRef.current) {
      const yearAdded = parseInt(yearAddedRef.current.value);
      if (!yearAdded) {
        return;
      }
      setIndicatorData(
        indicatorData.map((record) => {
          if (record.id === addYearTo) {
            return {
              ...record,
              years: [...record.years, { value: null, year: yearAdded }],
            };
          }
          return record;
        })
      );
    }
    setAddYearTo("");
  };

  const deleteYear = (id: string, year: number) => {
    setIndicatorData(
      indicatorData.map((record) => {
        if (record.id === id) {
          const years = [...record.years];
          years.splice(
            years.findIndex((item) => item.year === year),
            1
          );

          return { ...record, years };
        }
        return record;
      })
    );
  };

  const language = context && context.language ? context.language : "nor-NO";

  return (
    <div>
      <div className={" " + definition.identifier}>
        <label className="field-label">{definition.name}</label>
        {definition.description && (
          <i
            className="icon-info"
            data-for={definition.identifier + "-desciption"}
            data-tip=""
          ></i>
        )}
        {definition.description && (
          <ReactTooltip
            id={definition.identifier + "-desciption"}
            effect="solid"
            place="right"
            html={true}
            clickable={true}
            multiline={true}
            delayHide={500}
            className="tip"
          >
            {definition.description}
          </ReactTooltip>
        )}
        :
      </div>
      {allIndicators && indicatorData && (
        <div>
          <input
            type="hidden"
            name={definition.identifier}
            value={JSON.stringify(indicatorData)}
          />
          {indicatorData.map((record, index) => {
            const indicatorDef = getIndicatorDef(parseInt(record.indicator));

            return (
              <div
                key={record.id}
                style={{
                  border: "1px solid #cccccc",
                  borderRadius: 3,
                  backgroundColor: "#f9f6f0",
                  width: "70%",
                  marginBottom: 20,
                  padding: 10,
                }}
              >
                {mode === "edit" && (
                  <div style={{ float: "right" }}>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      title={language === "nor-NO" ? "Slett" : "Delete"}
                      onClick={() => deleteIndicator(record.id)}
                    >
                      <i className="fa fa-trash"></i>
                    </Button>
                  </div>
                )}
                <div style={{ fontSize: 16 }}>
                  <label>
                    {indicatorDef.name} ({indicatorDef.category_name}):
                  </label>
                </div>
                <div>
                  <table>
                    <tbody>
                      <tr>
                        {record.years.map((year) => (
                          <td>
                            <div>{year.year}</div>
                          </td>
                        ))}
                        {mode === "edit" &&
                          getRemainYears(record.id).length > 0 && (
                            <td>
                              <Button
                                style={{ marginTop: 10 }}
                                onClick={() => addYear(record.id)}
                                variant="light"
                                size="sm"
                              >
                                <i className="fa fa-plus"></i>{" "}
                                {language === "nor-NO"
                                  ? "Legg til år"
                                  : "Add year"}
                              </Button>
                            </td>
                          )}
                      </tr>
                      <tr>
                        {record.years.map((yearRecord) => {
                          return (
                            <td
                              key={yearRecord.year}
                              style={{ padding: "0px 10px 0px 5px" }}
                            >
                              <div
                                style={{
                                  display: "flex",
                                  gap: 10,
                                  alignItems: "center",
                                }}
                              >
                                <input
                                  type="number"
                                  disabled={mode === "view"}
                                  className="form-control"
                                  style={{ width: 80 }}
                                  value={
                                    yearRecord.value === null
                                      ? ""
                                      : yearRecord.value
                                  }
                                  onChange={(e) => {
                                    changeYearValue(
                                      record.id,
                                      yearRecord.year,
                                      "value",
                                      parseInt(e.target.value)
                                    );
                                  }}
                                />

                                <div>{indicatorDef.type === 2 ? "%" : ""}</div>
                                {mode === "edit" && (
                                  <div>
                                    <Button
                                      variant="light"
                                      size="sm"
                                      onClick={() =>
                                        deleteYear(record.id, yearRecord.year)
                                      }
                                    >
                                      <i className="fa fa-trash"></i>
                                    </Button>
                                  </div>
                                )}
                              </div>
                            </td>
                          );
                        })}
                        <td></td>
                      </tr>
                      <tr>
                        <td style={{ paddingTop: 10 }}>
                          <label>
                            {language === "nor-NO" ? "Mål:" : "Target:"}
                          </label>
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 10,
                            }}
                          >
                            <input
                              type="number"
                              disabled={mode === "view"}
                              className="form-control"
                              value={
                                record.target_value === null
                                  ? ""
                                  : record.target_value
                              }
                              onChange={(e) => {
                                changeValue(
                                  record.id,
                                  "target_value",
                                  parseInt(e.target.value)
                                );
                              }}
                              style={{ width: 80 }}
                            />
                            {indicatorDef.type === 2 ? "%" : ""}
                          </div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div style={{ paddingTop: 10 }}>
                    <label>
                      {language === "nor-NO" ? "Kommentar:" : "Comment:"}
                    </label>
                    <textarea
                      className="form-control"
                      rows={3}
                      disabled={mode === "view"}
                      value={record.comment}
                      onChange={(e) => {
                        changeValue(record.id, "comment", e.target.value);
                      }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
          {mode === "edit" && (
            <div style={{ marginTop: 20 }}>
              <SelectIndicator
                onAdded={addIndicator}
                key={indicatorData.length}
                language={language}
              />
            </div>
          )}
          <Modal show={!!addYearTo} onHide={() => setAddYearTo("")}>
            <Modal.Header closeButton translate="no">
              <Modal.Title>
                {language === "nor-NO" ? "Legg til år" : "Add year"}
              </Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div>
                <select className="form-control" ref={yearAddedRef}>
                  {getRemainYears(addYearTo).map((year: number) => (
                    <option key={year.toString()} value={year.toString()}>
                      {year}
                    </option>
                  ))}
                </select>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="primary" onClick={() => addYearConfirm()}>
                {language === "nor-NO" ? "Legg til" : "Add"}
              </Button>
              <Button variant="secondary" onClick={() => setAddYearTo("")}>
                {language === "nor-NO" ? "Lukk" : "Close"}
              </Button>
            </Modal.Footer>
          </Modal>
        </div>
      )}
    </div>
  );
};

const SelectIndicator = (props: {
  onAdded: (indicator: any) => void;
  language: string;
}) => {
  const { language } = props;
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<number | null>(null);
  const [categories, setCategories] = useState<any[]>([]);
  const [indicators, setIndicators] = useState<any[]>([]);

  useEffect(() => {
    loadCategories();
    loadIndicators();
  }, []);

  const loadCategories = () => {
    FetchWithAuth("eth/indicator3a/category/list", {
      method: "GET",
    }).then((data) => {
      setCategories(data.data);
    });
  };

  const loadIndicators = () => {
    FetchWithAuth("eth/indicator3a/list", {
      method: "GET",
    }).then((data) => {
      setIndicators(data.data);
    });
  };

  const indicatorList = React.useMemo(() => {
    let result: any[] = [];
    if (category) {
      result = indicators.filter((item) => item.category === category);
    } else {
      result = indicators;
    }
    result.sort((a, b) => a.name - b.name);
    return result;
  }, [category, indicators]);

  const [selected, setSelected] = useState<number | null>(null);

  return (
    <div>
      <Button size="sm" onClick={() => setOpen(true)}>
        {language === "nor-NO" ? "Legg til indikator" : "Add indicator"}
      </Button>

      <Modal size="lg" show={open} onHide={() => setOpen(false)}>
        <Modal.Header closeButton translate="no">
          <Modal.Title>
            {language === "nor-NO" ? "Legg til indikator" : "Add indicator"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div>
            <div>
              <Select
                placeholder={
                  language === "nor-NO" ? "Velg kategori" : "Select category"
                }
                options={[
                  {
                    label: language === "nor-NO" ? "Alle" : "All",
                    value: null,
                  },
                  ...categories.map((category) => ({
                    label: category.name,
                    value: category.id,
                  })),
                ]}
                onChange={(e: any) => {
                  setCategory(e.value);
                  setSelected(null);
                }}
              ></Select>
            </div>
            <div
              style={{
                marginTop: 10,
                padding: 10,
                maxHeight: 400,
                overflowY: "auto",
              }}
            >
              {indicatorList.map((indicator) => (
                <div key={indicator.id} style={{ display: "flex" }}>
                  <input
                    type="radio"
                    checked={selected === indicator.id}
                    id={"indicator" + indicator.id}
                    onChange={() => setSelected(indicator.id)}
                  />
                  <label
                    htmlFor={"indicator" + indicator.id}
                    style={{ marginLeft: 10 }}
                  >
                    {indicator.name}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              props.onAdded(
                indicatorList.find((indicator) => indicator.id === selected)
              );
              setOpen(false);
            }}
          >
            {language === "nor-NO" ? "Velg" : "Select"}
          </Button>
          <Button variant="cancel" onClick={() => setOpen(false)}>
            {language === "nor-NO" ? "Lukk" : "Close"}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};
